'use client';

import type { ComponentType, ReactElement } from 'react';
import { ModalLocation } from '@/constants/componentPresets';
import { SessionsDisplayMode } from '@/types/frontend-types';
import type { TeamDetail } from '@cabal/shared/domain/session/team/types.js';
import { ColorType } from '@/theme/colors';
import { Cmodal } from '../Cmodal';
import type { CsessionsProps } from '../Csessions';
import {
  ARCHIVED_SESSIONS_MODAL_TITLE,
  SESSIONS_MODAL_TITLE,
} from './Cpagelayout.constants';
import { SessionsModalTitleActions } from './SessionsModalTitleActions';

interface SessionsModalProps {
  SessionsComponent: ComponentType<CsessionsProps>;
  currentSessionId?: string;
  displayMode: SessionsDisplayMode;
  isSearchInitiated: boolean;
  isSessionsModalOpen: boolean;
  onDisplayModeToggle: () => void;
  onReplicateTeam: (teamConfig: TeamDetail) => void;
  onSearchQueryChange: (query: string) => void;
  onSearchToggle: () => void;
  onSessionCountChange: (count: number | null) => void;
  onSessionsModalClose: () => void;
  onStartNewSession?: () => void;
  searchQuery: string;
  sessionCount: number | null;
}

const SESSIONS_MODAL_TITLE_BY_DISPLAY_MODE: Record<SessionsDisplayMode, string> = {
  [SessionsDisplayMode.Active]: SESSIONS_MODAL_TITLE,
  [SessionsDisplayMode.Archived]: ARCHIVED_SESSIONS_MODAL_TITLE,
};

export function SessionsModal({
  SessionsComponent,
  currentSessionId,
  displayMode,
  isSearchInitiated,
  isSessionsModalOpen,
  onDisplayModeToggle,
  onReplicateTeam,
  onSearchQueryChange,
  onSearchToggle,
  onSessionCountChange,
  onSessionsModalClose,
  onStartNewSession,
  searchQuery,
  sessionCount,
}: SessionsModalProps): ReactElement {
  return (
    <Cmodal
      dataElScope="Cpagelayout/SessionsModal"
      visible={isSessionsModalOpen}
      location={ModalLocation.Left}
      background={ColorType.Black}
      backgroundOpacity={0.45}
      content={
        <SessionsComponent
          onSessionCountChange={onSessionCountChange}
          currentSessionId={currentSessionId}
          onClose={onSessionsModalClose}
          onStartNewSession={onStartNewSession}
          isSearchInitiated={isSearchInitiated}
          displayMode={displayMode}
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
          onReplicateTeam={onReplicateTeam}
        />
      }
      menuTitle={SESSIONS_MODAL_TITLE_BY_DISPLAY_MODE[displayMode]}
      titleExtra={sessionCount != null ? String(sessionCount) : undefined}
      titleRightComponent={
        <SessionsModalTitleActions
          displayMode={displayMode}
          isSearchInitiated={isSearchInitiated}
          onToggleDisplayMode={onDisplayModeToggle}
          onToggleSearch={onSearchToggle}
        />
      }
      onClose={onSessionsModalClose}
    />
  );
}
